import { useEffect, useState, useCallback } from 'react';
import { useGameStore } from '../stores/gameStore';
import { gameApi } from '../services/api';
import { requestMidgameAd, requestRewardedAd, useAdsEnabled } from '../services/crazygames';
import { Mission, MissionInstance, MissionInstanceStatus, MissionResult, MissionType } from '../types';

// ── Helpers ───────────────────────────────────────────────────────────────────

const TYPE_LABEL: Record<MissionType, string> = {
  [MissionType.DataTheft]:          'DATA THEFT',
  [MissionType.SystemInfiltration]: 'INFILTRATION',
  [MissionType.Cryptography]:       'CRYPTOGRAPHY',
  [MissionType.DDoS]:               'DDoS',
  [MissionType.SocialEngineering]:  'SOCIAL ENG.',
};

const TYPE_COLOR: Record<MissionType, string> = {
  [MissionType.DataTheft]:          '#60a5fa',
  [MissionType.SystemInfiltration]: '#f87171',
  [MissionType.Cryptography]:       '#a78bfa',
  [MissionType.DDoS]:               '#fb923c',
  [MissionType.SocialEngineering]:  '#34d399',
};

const OBJECTIVE: Record<MissionType, string> = {
  [MissionType.DataTheft]:          'Connect to the target, then Download the mission file',
  [MissionType.SystemInfiltration]: 'Connect to the target, then Delete the mission file',
  [MissionType.Cryptography]:       'Connect to the target, then Download the mission file',
  [MissionType.DDoS]:               'Run a DDoS attack on the target (after accepting)',
  [MissionType.SocialEngineering]:  'Connect to the target, then Download the mission file',
};

function difficultyLabel(d: number): { label: string; color: string } {
  if (d <= 3) return { label: 'EASY',   color: '#4ade80' };
  if (d <= 6) return { label: 'MEDIUM', color: '#fbbf24' };
  if (d <= 8) return { label: 'HARD',   color: '#f97316' };
  return          { label: 'EXPERT', color: '#f43f5e' };
}

const TIER_COLORS = ['#6b7280','#3b82f6','#8b5cf6','#ec4899','#f59e0b','#ef4444','#10b981','#14b8a6'];

function formatCountdown(deadlineIso: string): { text: string; urgent: boolean } {
  const secs = Math.max(0, Math.floor((new Date(deadlineIso).getTime() - Date.now()) / 1000));
  if (secs === 0) return { text: 'EXPIRED', urgent: true };
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return { text: `${m}m ${s.toString().padStart(2, '0')}s`, urgent: secs < 300 };
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function Missions() {
  const {
    missions, activeMissions, loadMissions, acceptMission,
    completeMission, cancelMission, isLoading, error,
    clearError, loadPlayer,
  } = useGameStore();
  const adsEnabled = useAdsEnabled();

  // Once-per-day gate: missionId → UTC day-key of last completion, fetched from its
  // dedicated container endpoint (no longer on the player doc).
  const [completions, setCompletions] = useState<Record<string, string>>({});
  const loadCompletions = useCallback(async () => {
    try {
      const res = await gameApi.getMissionCompletions();
      setCompletions((res.data ?? {}) as Record<string, string>);
    } catch { /* non-critical: leave the map as-is */ }
  }, []);

  // UTC day-key ("yyyy-MM-dd"); toISOString is always UTC. Missions completed on
  // this key are locked until the next UTC day.
  const todayUtc = new Date().toISOString().slice(0, 10);

  const [result,  setResult]  = useState<MissionResult | null>(null);
  const [busy,    setBusy]    = useState<string | null>(null);
  const [, setTick] = useState(0);
  const [filter,  setFilter]  = useState<MissionType | 'all'>('all');
  // Mission pending cancellation confirmation (in-app modal instead of window.confirm,
  // whose native dialog breaks the iframe/landscape view on portals).
  const [cancelTarget, setCancelTarget] = useState<MissionInstance | null>(null);

  // Load on mount so a direct page load / F5 works (data comes via the session
  // cookie; an invalid cookie is handled by the global 401 interceptor).
  useEffect(() => {
    loadMissions();
    loadPlayer();
    loadCompletions();
  }, [loadMissions, loadPlayer, loadCompletions]);

  // Countdown ticker
  useEffect(() => {
    if (!activeMissions.some(m => m.status === MissionInstanceStatus.Active)) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [activeMissions]);

  const handleAccept = useCallback(async (mission: Mission) => {
    setBusy(mission.id);
    setResult(null);
    clearError();
    await acceptMission(mission.id);
    setBusy(null);
  }, [acceptMission, clearError]);

  const handleComplete = useCallback(async (instance: MissionInstance) => {
    setBusy(instance.instanceId);
    setResult(null);
    clearError();
    const r = await completeMission(instance.instanceId, false);
    if (r) {
      setResult(r);
      await loadMissions();
      await loadCompletions();
      // Natural break: show a CrazyGames interstitial — no-op off CrazyGames, and
      // skipped if any ad (incl. a rewarded one) was shown in the last 5 minutes.
      void requestMidgameAd();
    }
    setBusy(null);
  }, [completeMission, clearError, loadMissions, loadCompletions]);

  // Complete via a CrazyGames rewarded ad: watch → +20% reward; if the ad can't be
  // shown or is closed early → base reward. Never triggers the interstitial (the
  // rewarded ad IS the ad, and it resets the interstitial cooldown).
  const handleCompleteWithAds = useCallback(async (instance: MissionInstance) => {
    setBusy(instance.instanceId);
    setResult(null);
    clearError();
    const watched = await requestRewardedAd();
    const r = await completeMission(instance.instanceId, watched);
    if (r) { setResult(r); await loadMissions(); await loadCompletions(); }
    setBusy(null);
  }, [completeMission, clearError, loadMissions, loadCompletions]);

  // Open the in-app confirmation modal (see cancelTarget) instead of window.confirm.
  const handleCancel = useCallback((instance: MissionInstance) => {
    setCancelTarget(instance);
  }, []);

  const confirmCancel = useCallback(async () => {
    if (!cancelTarget) return;
    const id = cancelTarget.instanceId;
    setCancelTarget(null);
    setBusy(id);
    await cancelMission(id);
    setBusy(null);
  }, [cancelTarget, cancelMission]);

  const activeList = activeMissions.filter(m => m.status === MissionInstanceStatus.Active);
  const missionTypes = [...new Set(missions.map(m => m.type))] as MissionType[];
  const visibleMissions = filter === 'all' ? missions : missions.filter(m => m.type === filter);
  const missionsByTier = visibleMissions.reduce<Record<number, Mission[]>>((acc, m) => {
    if (!acc[m.targetNpcTier]) acc[m.targetNpcTier] = [];
    acc[m.targetNpcTier].push(m);
    return acc;
  }, {});

  return (
    <div style={{ maxWidth: 900 }}>
      <h1 style={{ color: 'var(--primary-color)', marginBottom: '0.25rem' }}>Missions</h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
        Missions unlock by hacking NPCs. Each accepted mission creates files visible only to you on the target.
        Complete the objective before the timer expires to collect your reward. Each mission can be completed
        once per day (resets at 00:00 UTC).
      </p>

      {/* Result banner */}
      {result && (
        <div className={result.success ? 'success' : 'error'} style={{ marginBottom: '1.5rem' }}>
          <strong>{result.success ? 'Mission accomplished!' : 'Mission failed!'}</strong>
          <p style={{ margin: '0.25rem 0 0' }}>{result.message}</p>
          {result.success && (
            <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1.5rem', fontSize: '0.9rem' }}>
              <span style={{ color: '#4ade80' }}>+{result.experienceGained} XP</span>
              <span style={{ color: '#fbbf24' }}>+{result.creditsGained.toLocaleString()} Cr</span>
              <span style={{ color: '#60a5fa' }}>+{result.reputationGained} Rep</span>
            </div>
          )}
          <button onClick={() => setResult(null)} style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}>Dismiss</button>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="error" style={{ marginBottom: '1rem' }}>
          {error}
          <button onClick={clearError} style={{ marginLeft: '1rem', fontSize: '0.8rem' }}>✕</button>
        </div>
      )}

      {/* Active missions section */}
      {activeList.length > 0 && (
        <section style={{ marginBottom: '2rem' }}>
          <h2 style={{ color: '#fbbf24', marginBottom: '0.75rem', fontSize: '1rem' }}>
            ACTIVE MISSIONS ({activeList.length})
          </h2>
          {activeList.map(inst => {
            const countdown = formatCountdown(inst.deadline);
            const typeColor = TYPE_COLOR[inst.missionType] ?? '#9ca3af';
            const isBusy    = busy === inst.instanceId;
            return (
              <div key={inst.instanceId} className="card" style={{ marginBottom: '0.75rem', borderLeft: `3px solid ${typeColor}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem' }}>
                      <span style={{ color: typeColor, fontSize: '0.7rem', fontWeight: 700 }}>{TYPE_LABEL[inst.missionType]}</span>
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                        → {inst.targetNpcName} ({inst.targetNpcIp})
                      </span>
                    </div>
                    <strong style={{ fontSize: '0.95rem' }}>{inst.missionName}</strong>
                    <div style={{ marginTop: '0.35rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      Objective: <em style={{ color: '#e2e8f0' }}>{OBJECTIVE[inst.missionType]}</em>
                    </div>
                    {inst.missionType !== MissionType.DDoS && inst.missionFileName && (
                      <div style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: '#60a5fa' }}>
                        Mission file: <code>{inst.missionFileName}</code>
                      </div>
                    )}
                    <div style={{ marginTop: '0.4rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      Reward:&nbsp;
                      <span style={{ color: '#4ade80' }}>{inst.reward.experience} XP</span> ·&nbsp;
                      <span style={{ color: '#fbbf24' }}>{inst.reward.credits.toLocaleString()} Cr</span> ·&nbsp;
                      <span style={{ color: '#60a5fa' }}>{inst.reward.reputation} Rep</span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', minWidth: 110 }}>
                    <div style={{
                      fontSize: '1.1rem', fontFamily: 'monospace', fontWeight: 700,
                      color: countdown.urgent ? '#f43f5e' : '#4ade80',
                    }}>
                      {countdown.text}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>remaining</div>
                    <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                      <button
                        className="primary"
                        style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem' }}
                        onClick={() => handleComplete(inst)}
                        disabled={isBusy || countdown.text === 'EXPIRED'}
                      >
                        {isBusy ? '…' : 'Complete'}
                      </button>
                      {/* Rewarded-ad completion (+20%) — only when ads are enabled (CrazyGames). */}
                      {adsEnabled && (
                        <button
                          style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem', background: '#2e1065', border: '1px solid #7c3aed', color: '#c4b5fd', borderRadius: '4px', cursor: 'pointer' }}
                          onClick={() => handleCompleteWithAds(inst)}
                          disabled={isBusy || countdown.text === 'EXPIRED'}
                          title="Watch a short ad to complete with +20% reward"
                        >
                          {isBusy ? '…' : 'Complete (ADS) +20%'}
                        </button>
                      )}
                      <button
                        style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem', background: 'transparent', border: '1px solid #4b5563', color: '#9ca3af' }}
                        onClick={() => handleCancel(inst)}
                        disabled={isBusy}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </section>
      )}

      {/* Type filter */}
      {missions.length > 0 && (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <button
            onClick={() => setFilter('all')}
            style={{
              fontSize: '0.75rem', padding: '0.25rem 0.6rem',
              background: filter === 'all' ? 'var(--primary-color)' : 'transparent',
              border: '1px solid var(--primary-color)',
              color: filter === 'all' ? '#000' : 'var(--primary-color)',
            }}
          >
            ALL ({missions.length})
          </button>
          {missionTypes.map(t => (
            <button
              key={t}
              onClick={() => setFilter(t)}
              style={{
                fontSize: '0.75rem', padding: '0.25rem 0.6rem',
                background: filter === t ? TYPE_COLOR[t] : 'transparent',
                border: `1px solid ${TYPE_COLOR[t]}`,
                color: filter === t ? '#000' : TYPE_COLOR[t],
              }}
            >
              {TYPE_LABEL[t]} ({missions.filter(m => m.type === t).length})
            </button>
          ))}
        </div>
      )}

      {/* Mission list by tier */}
      {isLoading && <div className="loading">Loading missions...</div>}

      {!isLoading && missions.length === 0 && (
        <div className="card" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
          No missions available yet. Hack NPCs to unlock missions on their nodes.
          Start by connecting to <code>10.0.0.1</code> from the Remote panel.
        </div>
      )}

      {Object.entries(missionsByTier)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([tier, tierMissions]) => {
          const tierNum  = Number(tier);
          const tierColor = TIER_COLORS[tierNum] ?? '#6b7280';
          return (
            <section key={tier} style={{ marginBottom: '2rem' }}>
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem', marginBottom: '0.75rem' }}>
                <span style={{ background: tierColor, color: '#fff', padding: '0.15rem 0.5rem', borderRadius: 4, fontSize: '0.75rem', fontWeight: 700 }}>
                  T{tierNum + 1}
                </span>
                <span style={{ color: 'var(--text-muted)' }}>
                  Tier {tierNum + 1} — {tierMissions.length} mission{tierMissions.length !== 1 ? 's' : ''}
                </span>
              </h2>
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {tierMissions.map(mission => (
                  <MissionCard
                    key={mission.id}
                    mission={mission}
                    activeMissions={activeMissions}
                    busy={busy}
                    onAccept={handleAccept}
                    lockedToday={completions[mission.id] === todayUtc}
                  />
                ))}
              </div>
            </section>
          );
        })}

      {cancelTarget && (
        <CancelMissionModal
          missionName={cancelTarget.missionName}
          onConfirm={confirmCancel}
          onDismiss={() => setCancelTarget(null)}
        />
      )}
    </div>
  );
}

// ── Cancel-confirmation modal ───────────────────────────────────────────────────
// Replaces window.confirm, whose native dialog breaks the landscape / iframe view on
// the game portals. Click-outside and the Keep button dismiss it.
function CancelMissionModal({ missionName, onConfirm, onDismiss }: {
  missionName: string;
  onConfirm: () => void;
  onDismiss: () => void;
}) {
  return (
    <div
      onClick={onDismiss}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          background: '#0a0a0a', border: '1px solid var(--border-color)', borderRadius: '6px',
          width: '100%', maxWidth: '420px', padding: '1.25rem', fontFamily: 'monospace',
        }}
      >
        <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: '#e2e8f0' }}>Cancel mission?</h3>
        <p style={{ margin: '0 0 1.25rem', fontSize: '0.85rem', color: '#cbd5e1', lineHeight: 1.5 }}>
          Cancel <strong style={{ color: '#fff' }}>{missionName}</strong>? You will lose all progress and receive no reward.
        </p>
        <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button
            onClick={onDismiss}
            style={{ fontSize: '0.85rem', padding: '0.4rem 0.9rem', background: 'transparent', border: '1px solid #4b5563', color: '#e2e8f0', borderRadius: '4px', cursor: 'pointer' }}
          >
            Keep mission
          </button>
          <button
            onClick={onConfirm}
            style={{ fontSize: '0.85rem', padding: '0.4rem 0.9rem', background: '#7f1d1d', border: '1px solid #ef4444', color: '#fecaca', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
          >
            Cancel mission
          </button>
        </div>
      </div>
    </div>
  );
}

// ── MissionCard ───────────────────────────────────────────────────────────────

function MissionCard({ mission, activeMissions, busy, onAccept, lockedToday }: {
  mission: Mission;
  activeMissions: MissionInstance[];
  busy: string | null;
  onAccept: (m: Mission) => void;
  lockedToday: boolean;
}) {
  const diff      = difficultyLabel(mission.difficulty);
  const typeColor = TYPE_COLOR[mission.type] ?? '#9ca3af';
  const isBusy    = busy === mission.id;

  const activeInstance = activeMissions.find(
    m => m.missionId === mission.id && m.status === MissionInstanceStatus.Active
  );

  return (
    <div className="card" style={{ borderLeft: `3px solid ${typeColor}`, opacity: isBusy ? 0.7 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
            <span style={{ color: typeColor, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em' }}>
              {TYPE_LABEL[mission.type]}
            </span>
            <span style={{ color: diff.color, fontSize: '0.7rem', fontWeight: 700 }}>
              {diff.label} (D{mission.difficulty})
            </span>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>
              {mission.timeLimit}m limit · once per day
            </span>
          </div>

          <strong style={{ fontSize: '0.9rem' }}>{mission.name}</strong>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', margin: '0.2rem 0' }}>
            Target: {mission.targetNpcName} · <code style={{ fontSize: '0.75rem' }}>{mission.targetNpcIp}</code>
          </div>
          <p style={{ color: '#cbd5e1', fontSize: '0.8rem', margin: '0.2rem 0 0.4rem', lineHeight: 1.4 }}>
            {mission.description}
          </p>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            Objective: <em style={{ color: '#e2e8f0' }}>{OBJECTIVE[mission.type]}</em>
          </div>

          <div style={{ marginTop: '0.4rem', display: 'flex', gap: '1rem', fontSize: '0.8rem' }}>
            <span style={{ color: '#4ade80' }}>+{mission.reward.experience} XP</span>
            <span style={{ color: '#fbbf24' }}>+{mission.reward.credits.toLocaleString()} Cr</span>
            <span style={{ color: '#60a5fa' }}>+{mission.reward.reputation} Rep</span>
          </div>
        </div>

        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          {activeInstance ? (
            <span style={{ color: '#fbbf24', fontSize: '0.8rem', fontWeight: 600 }}>IN PROGRESS</span>
          ) : lockedToday ? (
            <div style={{ textAlign: 'right' }}>
              <span style={{ color: '#4ade80', fontSize: '0.8rem', fontWeight: 600 }}>DONE TODAY</span>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem', marginTop: '0.15rem' }}>
                resets 00:00 UTC
              </div>
            </div>
          ) : (
            <button
              className="primary"
              style={{ fontSize: '0.8rem', padding: '0.35rem 0.8rem' }}
              onClick={() => onAccept(mission)}
              disabled={isBusy}
            >
              {isBusy ? 'Accepting…' : 'Accept'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
